import React, { useState, useEffect } from "react";
import ReactAudioPlayer from "react-audio-player";
import TranscriptionConversation from "./TranscriptionConversation";
import TranscriptionSummary from "./TranscriptionSummary";
import SVG008 from "./008.svg";
import { FiPlayCircle } from "react-icons/fi";
import { EuiLoadingSpinner } from "@elastic/eui";
import conversation_example from "./conversation_example.json";
import CdrService from "../../../services/v2/cdr-service";

const TranscriptionMain = ({ id, callRecording }) => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [isFetchingChunks, setIsFetchingChunks] = useState(false); // Estado para la API
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [summarizationData, setSummarizationData] = useState(null); // Estado para el resumen
  const [error, setError] = useState(null); // Estado para errores
  const [conversationState, setConversationState] = useState({
    displayedMessages: [],
    currentMessage: "",
    currentChunkIndex: 0,
    isFinished: false,
    chunks: [], // Aquí se almacenan los chunks obtenidos de la API
  });

  const [audioSrc, setAudioSrc] = useState(null);

  useEffect(() => {
    if (callRecording) {
      // Crear un Object URL para el stream
      const objectUrl = URL.createObjectURL(callRecording);
      setAudioSrc(objectUrl);

      // Limpiar el Object URL al desmontar el componente
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [callRecording]);
  // Simular llamada a la API para obtener los chunks

  const handleStartTranscription = async () => {
    setIsTranscribing(true);
    setIsFetchingChunks(true);
    setError(null);

    try {
      // Llamar al servicio real de transcripción
      const response = await CdrService.getTranscription(id);

      // Actualizar el estado de la conversación con los chunks obtenidos
      setConversationState((prev) => ({
        ...prev,
        chunks: response?.data?.transcription?.chunks,
      }));

      // Actualizar el estado del resumen con el resumen recibido
      setSummarizationData(response?.data?.extraction?.data);

      // Cambiar al tab "conversation"
      setActiveTab("conversation");
      setIsConversationLoading(true);
    } catch (error) {
      if (error.response && error.response.status === 429) {
        setError("Daily transcription limit reached");
      } else {
        setError("Failed to transcribe the call. Please try again.");
      }
    } finally {
      setIsTranscribing(false);
      setIsFetchingChunks(false);
    }
  };

  return (
    <div className="relative w-full h-96 bg-white overflow-hidden p-1">
      {/* Contenedor superior */}
      <div className="absolute top-0 left-0 w-full h-12 flex items-end justify-between px-4">
        <div
          className={`absolute flex gap-2 items-center content-center ${
            isTranscribing || activeTab === null
              ? "top-1/2 left-1/2 transform -translate-x-1/2 mt-48"
              : "top-2 left-2 transform translate-x-0 translate-y-0"
          } transition-all duration-700 ease-in-out z-10`}
        >
          <img
            src={SVG008}
            alt="008"
            className={`${
              isTranscribing || activeTab === null ? "w-12 h-12" : "w-9 h-9"
            } transition-all duration-700`}
          />
          {isConversationLoading && <EuiLoadingSpinner size="m" />}
        </div>

        {!isTranscribing && activeTab !== null && (
          <div className="flex space-x-4 ml-auto">
            {["audioPlayer", "conversation", "summary"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                disabled={isFetchingChunks || isConversationLoading}
                className={`px-4 ring-1 ring-[#F02E65] py-2 text-sm font-semibold rounded-t-lg transition-all ${
                  activeTab === tab
                    ? "bg-[#F1B6C7] text-[#F02E65] border-b-2 border-[#F02E65]"
                    : `bg-gray-200 text-[#F02E65] ${
                        isFetchingChunks || isConversationLoading
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-[#F1B6C7] hover:text-[#F02E65]/80"
                      }`
                }`}
              >
                {tab === "audioPlayer" ? (
                  <FiPlayCircle size={16} />
                ) : (
                  tab.charAt(0).toUpperCase() + tab.slice(1)
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {!isTranscribing && activeTab !== null && (
        <div className="h-80 mt-[2.85em] ring-1 ring-[#F02E65] rounded-xl p-3">
          {activeTab === "conversation" && (
            <TranscriptionConversation
              conversationState={conversationState}
              setConversationState={setConversationState}
              isLoading={isConversationLoading}
              onFinishLoading={() => setIsConversationLoading(false)}
            />
          )}
          {activeTab === "summary" && (
            <TranscriptionSummary data={summarizationData} />
          )}
          {activeTab === "audioPlayer" && (
            <div className="flex items-center justify-center h-full">
              <ReactAudioPlayer src={audioSrc} controls />
            </div>
          )}
        </div>
      )}

      {activeTab === null && (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <ReactAudioPlayer src={audioSrc} controls />
          <div className="flex flex-col items-center">
            <button
              onClick={handleStartTranscription}
              className="px-6 py-3 mt-24 font-bold text-gray-400 bg-white rounded-lg shadow-md transition-transform transform hover:scale-105"
            >
              {isTranscribing ? (
                <div className="flex gap-2">
                  <EuiLoadingSpinner size="s" /> Loading
                </div>
              ) : (
                "Experience AI-Powered Transcription with 008q!"
              )}
            </button>
            {isTranscribing && (
              <div className="absolute text-center justify-center bottom-6 top-auto mt-4 text-sm text-blue-700">
                <span className="bg-blue-200 px-3 py-1 rounded-md">
                  Trial transcription in progress. Production speeds can be up
                  to 10x faster.
                </span>
              </div>
            )}
            {error && (
              <div className="absolute text-center justify-center bottom-6 top-auto mt-4 text-sm text-red-700">
                <span className="bg-red-200 px-3 py-1 rounded-md">{error}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptionMain;
